import {useEffect, useMemo, useState} from "react";
import {Copy, ExternalLink} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {
    useGetLicenseStripeUrlQuery,
    useGetProductsQuery,
    useMigrateLicenseToStripeMutation,
    VwpLicenseRead
} from "../../../../store/generated/api";
import {toast} from "sonner";
import {Input} from "../../../basic/input";
import {Label} from "../../../basic/label";
import {Button} from "../../../basic/button";
import {Combobox} from "../../../basic/combobox";

interface StripeBillingSectionProps {
    selectedLicense: VwpLicenseRead;
    editedLicenseData: Record<string, any>;
    setEditedLicenseData: (data: Record<string, any>) => void;
    onLicenseUpdated?: (license: VwpLicenseRead) => void;
}

// SMA support products that can be auto-matched from the license items to a Stripe catalog product by name
const SMA_SUPPORT_PRODUCT_NAMES = [
    "SMA+ for Polysun Designer",
    "SMA for Polysun Designer",
    "SMA for Polysun Standard",
];

export function StripeBillingSection({
                                          selectedLicense,
                                          editedLicenseData,
                                          setEditedLicenseData,
                                          onLicenseUpdated
                                      }: StripeBillingSectionProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [shouldFetchStripeUrl, setShouldFetchStripeUrl] = useState(false);
    const {data: stripeUrlData, isLoading: isLoadingStripeUrl} = useGetLicenseStripeUrlQuery(
        {licenseId: selectedLicense.id || ''},
        {skip: !shouldFetchStripeUrl || !selectedLicense.id}
    );
    const [migrateLicenseToStripe, {isLoading: isMigrating}] = useMigrateLicenseToStripeMutation();

    const {data: productsData} = useGetProductsQuery();
    const products = useMemo(() => productsData?.products ?? [], [productsData]);
    const productOptions = useMemo(() => products.map(product => ({value: product.id, label: product.name})), [products]);

    const [stripeProductId, setStripeProductId] = useState("");
    const smaCurrency = editedLicenseData.smaCurrency ?? "EUR";
    const customSmaPrice = editedLicenseData.customSmaPrice ?? "";
    const defaultQuantity = selectedLicense.quantity && selectedLicense.quantity > 0 ? selectedLicense.quantity : 1;
    const quantity = editedLicenseData.quantity ?? String(defaultQuantity);

    const canMigrate = selectedLicense.id &&
        !(editedLicenseData.subscriptionId ?? selectedLicense.subscriptionId) &&
        !(editedLicenseData.firstInvoiceId ?? selectedLicense.firstInvoiceId);

    const parsedCustomAmount = customSmaPrice === "" ? NaN : Number(customSmaPrice);
    const isCustomPriceValid = !isNaN(parsedCustomAmount) && isFinite(parsedCustomAmount) && parsedCustomAmount >= 0.01;
    const parsedQuantity = quantity === "" ? NaN : Number(quantity);
    const isQuantityValid = Number.isInteger(parsedQuantity) && parsedQuantity >= 1;
    // customSmaPrice is a total currency amount (up to 2 decimal places); the per-unit amount is derived from the quantity.
    const totalCents = isCustomPriceValid ? Math.round(parsedCustomAmount * 100) : 0;
    const isDivisible = isCustomPriceValid && isQuantityValid && totalCents % parsedQuantity === 0;
    const unitValue = isDivisible ? totalCents / parsedQuantity / 100 : null;
    const formattedUnitPrice = unitValue != null
        ? new Intl.NumberFormat(undefined, {style: 'currency', currency: smaCurrency}).format(unitValue)
        : null;
    // When not divisible, suggest the largest price ≤ entered that IS divisible by quantity in cents
    const suggestedCents = isCustomPriceValid && isQuantityValid && !isDivisible && parsedQuantity > 0
        ? totalCents - (totalCents % parsedQuantity)
        : null;
    const suggestedPrice = suggestedCents !== null && suggestedCents > 0 ? suggestedCents / 100 : null;
    const formattedSuggestedPrice = suggestedPrice != null
        ? new Intl.NumberFormat(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(suggestedPrice)
        : null;
    const canSubmitMigration = !!stripeProductId && isCustomPriceValid && isQuantityValid && isDivisible;

    // Reset the product selection whenever a different license is opened
    useEffect(() => {
        setStripeProductId("");
    }, [selectedLicense?.id]);

    // Preselect the Stripe product matching an SMA support item already on the license (by name)
    useEffect(() => {
        if (stripeProductId || !selectedLicense?.items || products.length === 0) return;

        const normalize = (value: string) => value.trim().toLowerCase();
        const targets = SMA_SUPPORT_PRODUCT_NAMES.map(normalize);
        const matchedItem = selectedLicense.items.find(item => item.name && targets.includes(normalize(item.name)));
        if (!matchedItem) return;

        const target = normalize(matchedItem.name);
        const match = products.find(p => p.name && normalize(p.name) === target);
        if (match) {
            setStripeProductId(match.id);
        }
    }, [selectedLicense, products, stripeProductId]);

    const handleMigrateToStripe = async () => {
        if (!stripeProductId) {
            showErrorToast({message: t('admin.licenses.editLicense.errors.selectStripeProduct')});
            return;
        }
        if (!isCustomPriceValid) {
            showErrorToast({message: t('admin.licenses.editLicense.invalidCustomSmaPrice')});
            return;
        }
        if (!isQuantityValid) {
            showErrorToast({message: t('admin.licenses.editLicense.errors.quantityMin')});
            return;
        }
        if (!isDivisible) {
            showErrorToast({message: t('admin.licenses.editLicense.totalNotDivisible')});
            return;
        }

        try {
            const result = await migrateLicenseToStripe({
                licenseId: selectedLicense.id,
                vwpMigrateLicenseToStripeRequest: {
                    stripeProductId,
                    smaCurrency: smaCurrency,
                    customSmaPrice: parsedCustomAmount,
                    quantity: parsedQuantity
                }
            }).unwrap();
            toast.success(t('admin.licenses.editLicense.migrateToStripeSuccess'));
            onLicenseUpdated?.(result);
            handleOpenInStripe();
        } catch (error) {
            handleApiError(error, {
                fallbackMessage: t('admin.licenses.editLicense.migrateToStripeError')
            });
        }
    };

    const handleCopyFirstInvoiceId = () => {
        const value = editedLicenseData.firstInvoiceId ?? selectedLicense.firstInvoiceId;
        if (value) {
            navigator.clipboard.writeText(value);
            toast.success(t('admin.licenses.editLicense.firstInvoiceIdCopied'));
        }
    };

    const handleCopySubscriptionId = () => {
        const value = editedLicenseData.subscriptionId ?? selectedLicense.subscriptionId;
        if (value) {
            navigator.clipboard.writeText(value);
            toast.success(t('admin.licenses.editLicense.subscriptionIdCopied'));
        }
    };

    const handleOpenInStripe = async () => {
        if (!selectedLicense.id) return;

        setShouldFetchStripeUrl(true);
    };

    useEffect(() => {
        if (stripeUrlData && shouldFetchStripeUrl) {
            if (stripeUrlData.url) {
                window.open(stripeUrlData.url, '_blank');
            } else {
                toast.info(t('admin.licenses.noStripeData'));
            }
            setShouldFetchStripeUrl(false);
        }
    }, [stripeUrlData, shouldFetchStripeUrl, t]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="first-invoice-id">{t('admin.licenses.editLicense.firstInvoiceId')}</Label>
                    <div className="flex gap-2">
                        <Input
                            id="first-invoice-id"
                            type="text"
                            value={editedLicenseData.firstInvoiceId ?? selectedLicense.firstInvoiceId ?? ""}
                            onChange={(e) => setEditedLicenseData({
                                ...editedLicenseData,
                                firstInvoiceId: e.target.value || undefined
                            })}
                            placeholder={t('admin.licenses.editLicense.enterFirstInvoiceId')}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleCopyFirstInvoiceId}
                            disabled={!editedLicenseData.firstInvoiceId && !selectedLicense.firstInvoiceId}
                            title={t('common.copy')}
                        >
                            <Copy className="h-4 w-4"/>
                        </Button>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="subscription-id">{t('admin.licenses.editLicense.subscriptionId')}</Label>
                    <div className="flex gap-2">
                        <Input
                            id="subscription-id"
                            type="text"
                            value={editedLicenseData.subscriptionId ?? selectedLicense.subscriptionId ?? ""}
                            onChange={(e) => setEditedLicenseData({
                                ...editedLicenseData,
                                subscriptionId: e.target.value || undefined
                            })}
                            placeholder={t('admin.licenses.editLicense.enterSubscriptionId')}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleCopySubscriptionId}
                            disabled={!editedLicenseData.subscriptionId && !selectedLicense.subscriptionId}
                            title={t('common.copy')}
                        >
                            <Copy className="h-4 w-4"/>
                        </Button>
                    </div>
                </div>
            </div>

            <div className="flex gap-2">
                <Button
                    variant="outline"
                    onClick={handleOpenInStripe}
                    disabled={!selectedLicense.id || isLoadingStripeUrl}
                    className="flex-1"
                >
                    <ExternalLink className="h-4 w-4 mr-2"/>
                    {t('admin.licenses.openInStripe')}
                </Button>

                {canMigrate && (
                    <div className="flex-1 space-y-3 border rounded-lg p-4">
                        <div className="space-y-1">
                            <h4 className="font-semibold">{t('admin.licenses.editLicense.migrationSectionTitle')}</h4>
                            <p className="text-sm text-muted-foreground">
                                {t('admin.licenses.editLicense.migrationSectionDescription')}
                            </p>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="stripe-product">{t('admin.licenses.editLicense.stripeProduct')}</Label>
                            <Combobox
                                options={productOptions}
                                value={stripeProductId}
                                onValueChange={setStripeProductId}
                                placeholder={t('admin.licenses.editLicense.selectStripeProduct')}
                                searchPlaceholder={t('admin.licenses.editLicense.searchStripeProduct')}
                                emptyText={t('admin.licenses.editLicense.noStripeProducts')}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                                <Label htmlFor="sma-currency">{t('admin.licenses.editLicense.smaCurrency')}</Label>
                                <select
                                    id="sma-currency"
                                    className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                    value={smaCurrency}
                                    onChange={(e) => setEditedLicenseData({
                                        ...editedLicenseData,
                                        smaCurrency: e.target.value
                                    })}
                                >
                                    <option value="EUR">EUR</option>
                                    <option value="CHF">CHF</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="migration-quantity">{t('admin.licenses.editLicense.quantity')}</Label>
                                <Input
                                    id="migration-quantity"
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={quantity}
                                    onChange={(e) => setEditedLicenseData({
                                        ...editedLicenseData,
                                        quantity: e.target.value
                                    })}
                                />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="custom-sma-price">
                                {t('admin.licenses.editLicense.customSmaPrice')}
                            </Label>
                            <Input
                                id="custom-sma-price"
                                type="number"
                                min={0.01}
                                step={0.01}
                                value={customSmaPrice}
                                onChange={(e) => setEditedLicenseData({
                                    ...editedLicenseData,
                                    customSmaPrice: e.target.value
                                })}
                                placeholder={t('admin.licenses.editLicense.customSmaPricePlaceholder')}
                            />
                            {formattedUnitPrice && (
                                <p className="text-sm text-muted-foreground">
                                    {t('admin.licenses.editLicense.unitPrice')}: {formattedUnitPrice}
                                </p>
                            )}
                            {isCustomPriceValid && isQuantityValid && !isDivisible && (
                                <p className="text-sm text-destructive">
                                    {t('admin.licenses.editLicense.totalNotDivisible')}
                                    {formattedSuggestedPrice && (
                                        <> {t('admin.licenses.editLicense.suggestedPrice')}{' '}
                                            <button
                                                type="button"
                                                className="underline font-medium"
                                                onClick={() => setEditedLicenseData({
                                                    ...editedLicenseData,
                                                    customSmaPrice: String(suggestedPrice)
                                                })}
                                            >
                                                {formattedSuggestedPrice}
                                            </button>
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                        <Button
                            variant="default"
                            onClick={handleMigrateToStripe}
                            disabled={isMigrating || !canSubmitMigration}
                            className="w-full"
                        >
                            <ExternalLink className="h-4 w-4 mr-2"/>
                            {isMigrating ? t('admin.licenses.editLicense.migratingToStripe') : t('admin.licenses.editLicense.migrateToStripe')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
